// Pure, framework-free logic for athletes (see `features/athletes.md`).
// An athlete is a person profile owned by exactly one company; the company
// creates and manages it. Every athlete carries a platform-wide personal
// number, and can additionally be marked as a coach. The free-text description
// belongs to the person, coach or not. Kept free of React/persistence imports so
// it is shared by the client (form validation, age display) and the server
// (enforcement) alike.

import type { AthleteInput, Gender } from './types';
import { REGISTRATION_MIN_BIRTH_YEAR } from './types';
import { isRealIsoDate } from './registration';

/** Every athlete gender, in display order (competition categories are gendered). */
export const GENDERS: readonly Gender[] = ['MALE', 'FEMALE'];

/** Whether a string is a known gender code. */
export function isGender(value: string): value is Gender {
  return (GENDERS as readonly string[]).includes(value);
}

/**
 * The letters allowed in a personal number. Deliberately restricted to the
 * capitals whose glyphs are identical in the Latin and Cyrillic alphabets
 * (Latin A B C E H K M O P T X ↔ Cyrillic А В С Е Н К М О Р Т Х), so a number
 * reads — and can be typed — the same on either keyboard. Stored canonically
 * as the Latin code points; `normalizePersonalNumber` folds Cyrillic input.
 */
export const PERSONAL_NUMBER_LETTERS = 'ABCEHKMOPTX';

/** Digits following the letter in a personal number. */
export const PERSONAL_NUMBER_DIGITS = 8;

// Cyrillic capitals that look identical to an allowed Latin letter, mapped to
// the canonical Latin code point (index-aligned with PERSONAL_NUMBER_LETTERS).
const CYRILLIC_TWINS = 'АВСЕНКМОРТХ';

const PERSONAL_NUMBER_PATTERN = new RegExp(
  `^[${PERSONAL_NUMBER_LETTERS}]\\d{${PERSONAL_NUMBER_DIGITS}}$`,
);

/**
 * Normalize raw personal-number input to its canonical form: trimmed,
 * uppercased, with a Cyrillic look-alike letter folded to its Latin twin.
 * Purely lexical — the result may still be invalid (check with
 * `isValidPersonalNumber`).
 */
export function normalizePersonalNumber(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/./gu, (ch) => {
      const twin = CYRILLIC_TWINS.indexOf(ch);
      return twin >= 0 ? PERSONAL_NUMBER_LETTERS[twin] : ch;
    });
}

/** Whether `value` is a canonical personal number (allowed letter + 8 digits). */
export function isValidPersonalNumber(value: string): boolean {
  return PERSONAL_NUMBER_PATTERN.test(value);
}

/**
 * Generate a random personal number, e.g. "H67890954". Uniqueness is not
 * guaranteed here — the number is the athlete's primary key, so the caller
 * (the server's athlete service) retries on the rare collision. `random` is
 * injectable for deterministic tests.
 */
export function generatePersonalNumber(random: () => number = Math.random): string {
  const letter =
    PERSONAL_NUMBER_LETTERS[Math.floor(random() * PERSONAL_NUMBER_LETTERS.length)];
  let digits = '';
  for (let i = 0; i < PERSONAL_NUMBER_DIGITS; i += 1) {
    digits += Math.floor(random() * 10);
  }
  return `${letter}${digits}`;
}

/** Max length accepted for an athlete's full name. */
export const ATHLETE_NAME_MAX = 120;
/** Max length accepted for an athlete's or coach's free-text description. */
export const COACH_DESCRIPTION_MAX = 2000;
/** Maximum number of photos on an athlete's profile. */
export const ATHLETE_PHOTO_MAX = 4;

/**
 * Age in whole years at `now` for an ISO `YYYY-MM-DD` date of birth, or null
 * when the date is malformed. Computed rather than stored so it can never
 * drift out of sync with the date of birth.
 */
export function athleteAge(dateOfBirth: string, now: number = Date.now()): number | null {
  if (!isRealIsoDate(dateOfBirth)) return null;
  const [year, month, day] = dateOfBirth.split('-').map(Number);
  const today = new Date(now);
  let age = today.getUTCFullYear() - year;
  const monthDiff = today.getUTCMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < day)) {
    age -= 1;
  }
  return age;
}

/** Per-field validation messages; an absent key means that field is valid. */
export interface AthleteErrors {
  fullName?: string;
  gender?: string;
  dateOfBirth?: string;
  coachDescription?: string;
  photoUrls?: string;
}

/** A validated, normalized athlete profile — `gender` narrowed to a known code. */
export interface ValidAthleteInput extends AthleteInput {
  gender: Gender;
}

export interface AthleteValidation {
  valid: boolean;
  errors: AthleteErrors;
  /** Normalized, trimmed fields — present only when `valid` is true. */
  value: ValidAthleteInput | null;
}

/**
 * Validate and normalize the raw fields of an athlete profile form. Mirrors
 * `validateRegistration`: `now` (epoch ms) is injected so date-of-birth bounds
 * are testable. The description is kept whatever the coach mark says — every
 * athlete has a profile page and may maintain their own bio.
 */
export function validateAthlete(
  input: AthleteInput,
  now: number = Date.now(),
): AthleteValidation {
  const errors: AthleteErrors = {};

  const fullName = input.fullName.trim();
  if (fullName.length === 0) {
    errors.fullName = 'Full name is required.';
  } else if (fullName.length > ATHLETE_NAME_MAX) {
    errors.fullName = `Full name must be ${ATHLETE_NAME_MAX} characters or fewer.`;
  }

  const gender = (input.gender ?? '').trim();
  if (gender.length === 0) {
    errors.gender = 'Gender is required.';
  } else if (!isGender(gender)) {
    errors.gender = 'Select a valid gender.';
  }

  const dateOfBirth = input.dateOfBirth.trim();
  if (dateOfBirth.length === 0) {
    errors.dateOfBirth = 'Date of birth is required.';
  } else if (!isRealIsoDate(dateOfBirth)) {
    errors.dateOfBirth = 'Enter a valid date of birth.';
  } else {
    const year = Number(dateOfBirth.slice(0, 4));
    const dobMs = Date.parse(`${dateOfBirth}T00:00:00Z`);
    if (year < REGISTRATION_MIN_BIRTH_YEAR) {
      errors.dateOfBirth = 'Enter a valid date of birth.';
    } else if (dobMs > now) {
      errors.dateOfBirth = 'Date of birth cannot be in the future.';
    }
  }

  const isCoach = input.isCoach === true;
  // Kept for athletes as well as coaches: since every athlete has a profile page
  // and can maintain their own bio (see `features/athlete-access.md`), this is
  // the person's free-text description, not a coach-only field. It was cleared
  // for non-coaches when only coaches had somewhere to show it.
  const coachDescription = (input.coachDescription ?? '').trim();
  if (coachDescription.length > COACH_DESCRIPTION_MAX) {
    errors.coachDescription = `A description must be ${COACH_DESCRIPTION_MAX} characters or fewer.`;
  }

  const photoUrls = input.photoUrls ?? [];
  if (photoUrls.length > ATHLETE_PHOTO_MAX) {
    errors.photoUrls = `An athlete can have at most ${ATHLETE_PHOTO_MAX} photos.`;
  } else if (photoUrls.some((url) => typeof url !== 'string' || url.trim().length === 0)) {
    errors.photoUrls = 'Photo entries must be non-empty URLs.';
  }

  const valid = Object.keys(errors).length === 0;
  return {
    valid,
    errors,
    value: valid
      ? {
          fullName,
          gender: gender as Gender,
          dateOfBirth,
          isCoach,
          coachDescription,
          photoUrls: photoUrls.map((url) => url.trim()),
        }
      : null,
  };
}
